/**
 * A tab opened before a deploy still runs the old build. Its screens are loaded on demand (routes/index.tsx lazy()), so
 * the first screen it has not loaded yet asks for a file of the old build — which the new deploy may no longer serve —
 * and the screen fails with "Something went wrong", which "Try Again" cannot fix (it asks for the same missing file).
 * Seen on the rehearsal copy, 16 Oct 2026: a staff member's tab from before the 11:34 upgrade failed on a subscriber
 * page at 11:58.
 *
 * The cure is a full reload, which fetches the new index.html and with it the new files. It is done once
 * automatically; a second failure within a minute is shown as before (a reload loop would hide a real outage).
 */

/** The messages browsers give for a script file that could not be fetched (Chrome, Firefox, Safari, Vite preload). */
const STALE_PATTERNS = [
  /Failed to fetch dynamically imported module/i,
  /error loading dynamically imported module/i,
  /Importing a module script failed/i,
  /Unable to preload CSS/i,
  /Loading (CSS )?chunk [\w-]+ failed/i,
];

export function isStaleBuildError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : typeof error === 'string' ? error : '';
  return !!message && STALE_PATTERNS.some((p) => p.test(message));
}

export const RELOAD_KEY = 'isp_stale_build_reload_at';
/** One automatic reload a minute at most. */
export const RELOAD_WINDOW_MS = 60_000;

/** Whether a reload may be tried now, given when the last automatic one happened (ms since epoch, or null). */
export function mayReload(lastReloadAt: number | null, now: number): boolean {
  return lastReloadAt === null || !Number.isFinite(lastReloadAt) || now - lastReloadAt > RELOAD_WINDOW_MS;
}

/** Reload once for a stale build. True when a reload was started (the caller shows nothing more). */
export function reloadForStaleBuild(error: unknown): boolean {
  if (!isStaleBuildError(error)) return false;
  let last: number | null = null;
  try {
    const raw = sessionStorage.getItem(RELOAD_KEY);
    last = raw === null ? null : Number(raw);
  } catch { /* storage blocked: still reload once, the window guard below just cannot remember */ }
  const now = Date.now();
  if (!mayReload(last, now)) return false;
  try { sessionStorage.setItem(RELOAD_KEY, String(now)); } catch { /* see above */ }
  window.location.reload();
  return true;
}

/** Vite's own signal for a failed on-demand file (it fires before the import rejects). */
export function installStaleBuildReload(): void {
  window.addEventListener('vite:preloadError', (event) => {
    const e = event as Event & { payload?: unknown };
    if (reloadForStaleBuild(e.payload ?? new Error('Failed to fetch dynamically imported module'))) event.preventDefault();
  });
}
